import { useRef, useState, useEffect, useCallback } from "react";
import { Box, Stack } from "@mui/material";

export const DemoVideoPlayer = () => {
  const craneVideoRef = useRef<HTMLVideoElement>(null);
  const uiVideoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayPause = useCallback(() => {
    if (!craneVideoRef.current || !uiVideoRef.current) return;

    if (isPlaying) {
      craneVideoRef.current.pause();
      uiVideoRef.current.pause();
    } else {
      void craneVideoRef.current.play();
      void uiVideoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!craneVideoRef.current || !uiVideoRef.current) return;

    // Sync the other video if the time difference is more than 0.1 seconds
    const timeDiff = Math.abs(
      craneVideoRef.current.currentTime - uiVideoRef.current.currentTime
    );
    if (timeDiff > 0.1) {
      if (e.target === craneVideoRef.current) {
        uiVideoRef.current.currentTime = craneVideoRef.current.currentTime;
      } else {
        craneVideoRef.current.currentTime = uiVideoRef.current.currentTime;
      }
    }
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        event.preventDefault(); // Prevent page scrolling
        handlePlayPause();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handlePlayPause]);

  // Auto-play when component mounts
  useEffect(() => {
    const playVideos = async () => {
      if (!craneVideoRef.current || !uiVideoRef.current) return;

      try {
        // Wait for both videos to be loaded
        await Promise.all([
          craneVideoRef.current.play(),
          uiVideoRef.current.play(),
        ]);
        setIsPlaying(true);
      } catch (error) {
        console.error("Error auto-playing videos:", error);
      }
    };

    void playVideos();
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2}>
        <Box
          sx={{
            width: "75%",
            bgcolor: "#1a1a1a",
            borderRadius: "20px",
            p: "25px 25px 45px 25px", // More padding at bottom for controls
            boxShadow: "0 0 20px rgba(0,0,0,0.5)",
            position: "relative",
            overflow: "hidden", // Keep reflections inside the TV
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: "20px",
              border: "15px solid #303030",
              boxSizing: "border-box",
              background: "linear-gradient(145deg, #383838 0%, #303030 100%)",
            },
            // TV Stand
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-40px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "40%",
              height: "40px",
              background:
                "linear-gradient(to right, #2a2a2a, #383838, #2a2a2a)",
              clipPath: "polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)",
              zIndex: -1,
            },
            // Power indicator light
            "& .power-light": {
              position: "absolute",
              bottom: "10px",
              right: "20px",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: isPlaying ? "#4CAF50" : "#ff443a",
              boxShadow: isPlaying
                ? "0 0 10px #4CAF50, 0 0 5px #4CAF50"
                : "0 0 10px #ff443a, 0 0 5px #ff443a",
              transition: "all 0.3s ease",
              zIndex: 2,
            },
            // Brand logo
            "& .tv-brand": {
              position: "absolute",
              bottom: "10px",
              left: "20px",
              color: "#666",
              fontSize: "14px",
              fontFamily: "Arial",
              fontWeight: "bold",
              zIndex: 2,
            },
            // Control buttons
            "& .tv-controls": {
              position: "absolute",
              bottom: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: "10px",
              zIndex: 2,
            },
            "& .control-button": {
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "#444",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#666",
              },
            },
            // Screen reflection effect
            "& .screen-reflection": {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(145deg, rgba(255,255,255,0.03) 0%, transparent 80%)",
              pointerEvents: "none",
              zIndex: 3, // Put reflection above video
            },
          }}
        >
          {/* TV Elements */}
          <div className="power-light" />
          <div className="tv-brand" role="heading">
            SCRAPVISION
          </div>
          <div className="tv-controls">
            <div className="control-button" />
            <div className="control-button" />
            <div className="control-button" />
          </div>
          <div className="screen-reflection" />
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              // 16:9 aspect ratio container
              width: "100%",
              pt: "56.25%", // 9/16 = 0.5625 = 56.25%
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            >
              <video
                ref={craneVideoRef}
                width="100%"
                onTimeUpdate={handleTimeUpdate}
                onClick={handlePlayPause}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "fill",
                  cursor: "pointer",
                  borderRadius: "8px",
                }}
                loop
                title="Crane View"
                muted
              >
                <source
                  src="https://storage.cloud.google.com/fdm-scrap-chef-prod-resources/public/in-app-demo_crane-view.mp4"
                  type="video/mp4"
                />
              </video>
            </Box>
          </Box>
        </Box>
        <video
          height={300}
          width="25%"
          ref={uiVideoRef}
          onTimeUpdate={handleTimeUpdate}
          onClick={handlePlayPause}
          style={{ cursor: "pointer" }}
          loop
          title="UI View"
          muted
        >
          <source
            src="https://storage.cloud.google.com/fdm-scrap-chef-prod-resources/public/in-app-demo_ui-view.mp4"
            type="video/mp4"
          />
        </video>
      </Stack>
    </Box>
  );
};
